import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import type { Readable } from 'stream';

// Disable default body parser for multipart/form-data
export const config = {
  api: {
    bodyParser: false,
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_BUCKET = 'documentos';

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function readRawBody(req: NextApiRequest): Promise<Buffer> {
  const chunks: Buffer[] = [];
  for await (const chunk of req as unknown as Readable) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks);
}

interface ParsedMultipart {
  fields: Record<string, string>;
  file?: { filename: string; content: Buffer; contentType: string };
}

async function parseMultipart(req: NextApiRequest): Promise<ParsedMultipart> {
  const contentType = req.headers['content-type'] || '';
  const boundaryMatch = contentType.match(/boundary=(.+)/);
  if (!boundaryMatch) {
    throw new Error('No se encontró boundary en el Content-Type multipart.');
  }

  const boundary = boundaryMatch[1];
  const rawBody = await readRawBody(req);
  const bodyStr = rawBody.toString('latin1');
  const parts = bodyStr.split(`--${boundary}`).filter(
    (p) => p.trim() !== '' && p.trim() !== '--'
  );

  const result: ParsedMultipart = { fields: {} };

  for (const part of parts) {
    const headerEnd = part.indexOf('\r\n\r\n');
    if (headerEnd === -1) continue;

    const headerSection = part.substring(0, headerEnd);
    const bodySection = part.substring(headerEnd + 4).replace(/\r\n$/, '');

    const nameMatch = headerSection.match(/name="([^"]+)"/);
    if (!nameMatch) continue;
    const fieldName = nameMatch[1];

    const filenameMatch = headerSection.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      const contentTypeMatch = headerSection.match(/Content-Type:\s*(.+)/i);
      const start = rawBody.indexOf(
        Buffer.from('\r\n\r\n', 'latin1'),
        rawBody.indexOf(Buffer.from(headerSection.substring(0, 40), 'latin1'))
      ) + 4;
      const endMarker = Buffer.from(`\r\n--${boundary}`, 'latin1');
      const end = rawBody.indexOf(endMarker, start);
      result.file = {
        filename: filenameMatch[1],
        content: rawBody.subarray(start, end !== -1 ? end : undefined),
        contentType: contentTypeMatch ? contentTypeMatch[1].trim() : 'application/octet-stream',
      };
    } else {
      result.fields[fieldName] = bodySection;
    }
  }

  return result;
}

// ─── Handler ─────────────────────────────────────────────────────────────────

/**
 * POST /api/documents/upload
 *
 * Accepts multipart/form-data with a single file.
 * Uploads to Supabase Storage and creates a document record.
 *
 * Form fields:
 *   - file: the document file (required)
 *   - clienteId: string (required)
 *   - tipo: string (required)
 *   - notas: string (optional)
 *   - asesorId: string (optional, defaults to authenticated user id)
 *
 * Returns: { documentoId: string }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido.' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // Parse multipart form data
    const formData = await parseMultipart(req);

    const clienteId = formData.fields['clienteId'];
    const tipo = formData.fields['tipo'];
    const notas = formData.fields['notas'] || '';
    const asesorId = formData.fields['asesorId'] || user.id;

    if (!clienteId) {
      return res.status(400).json({ error: 'clienteId es requerido.' });
    }

    if (!tipo) {
      return res.status(400).json({ error: 'tipo es requerido.' });
    }

    if (!formData.file) {
      return res.status(400).json({ error: 'No se proporcionó archivo.' });
    }

    const { filename, content, contentType } = formData.file;

    // Sanitize filename and build storage path
    const sanitizedFileName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${clienteId}/${Date.now()}_${sanitizedFileName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabaseAdmin.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, content, {
        contentType,
        upsert: false,
      });

    if (uploadError) {
      console.error('[upload] Error subiendo archivo:', uploadError);
      return res.status(500).json({ error: `Error al subir el archivo: ${uploadError.message}` });
    }

    // Get public URL
    const { data: urlData } = supabaseAdmin.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(filePath);
    const publicUrl = urlData?.publicUrl ?? '';

    // Insert document record
    const { data: insertData, error: insertError } = await supabaseAdmin
      .from('documentos')
      .insert({
        nombre: filename,
        cliente_id: clienteId,
        asesor_id: asesorId,
        tipo,
        estado: 'pendiente',
        procesado_ia: false,
        tamanio: content.length,
        url_archivo: publicUrl,
      })
      .select('id')
      .single();

    if (insertError) {
      console.error('[upload] Error registrando documento:', insertError);
      return res.status(500).json({ error: `Error al registrar el documento: ${insertError.message}` });
    }

    return res.status(200).json({ documentoId: insertData.id });
  } catch (error: any) {
    console.error('[upload] Error:', error);
    return res.status(500).json({
      error: error.message || 'Error al procesar la subida del archivo.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
